import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import LazyLoad from "react-lazyload";

import { UitkHeading } from "@egds/react-core/text";
import { UitkCard, UitkCardPlaceholder } from "@egds/react-core/cards";

import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { withStores } from "stores";
import { DestinationVideoProps } from "./typings";
import { DestinationVideoFlexModuleResult } from "typings/microserviceModels/destination-video-flex-module";
import { observer } from "mobx-react";
import { UitkSpacing } from "@egds/react-core/spacing";

const DestinationVideo = withStores(
  "analytics",
  "context",
  "flexModuleModelStore",
  "flexViewModel"
)(
  observer((props: DestinationVideoProps) => {
    const { templateComponent, flexModuleModelStore, analytics, context, flexViewModel } = props;

    const pageName = flexViewModel.trackingContext.pageName;
    const {
      metadata: { id },
    } = templateComponent;
    const model = flexModuleModelStore.getModel(id) as DestinationVideoFlexModuleResult | null;
    const moduleName = id || "editorial";

    if (!model) {
      return null;
    }

    const { title, embeddedUrl: videoUrl } = model;
    const { formatText } = useLocalization();

    const alternateTitle = (locationName: string) => formatText("destinationVideo.video.forDestination", locationName);

    // This is covered in e2e test
    /* istanbul ignore next */
    React.useEffect(() => {
      const trackVideoClick = () => {
        const { activeElement } = document;
        if (activeElement?.id === `${id}-destination-video-iframe`) {
          const flexTrackingInfo: FlexTrackingInfo = {
            moduleName: pageName,
            action: Action.CLICK,
            rfrr: `${pageName}.${moduleName}.video.content`,
          };

          sendDelayedTrackEvent(flexTrackingInfo, analytics);
        }
      };

      window.addEventListener("blur", trackVideoClick);

      return () => {
        window.removeEventListener("blur", trackVideoClick);
      };
    }, []);

    return (
      <div className="DestinationVideo" id={id}>
        <UitkHeading tag="h2" size={4}>
          {title || alternateTitle(context.searchContext.location.localizedName)}
        </UitkHeading>

        <UitkSpacing margin={{ blockstart: "four" }}>
          <div data-testid="destination-video-test-iframe">
            <LazyLoad
              offset={500}
              height="100%"
              once
              placeholder={
                <UitkCard>
                  <UitkCardPlaceholder aspectRatio="r1-2" />
                </UitkCard>
              }
            >
              <div className="video-container">
                <iframe
                  id={`${id}-destination-video-iframe`}
                  title={alternateTitle(context.searchContext.location.defaultName)}
                  src={videoUrl}
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </LazyLoad>
          </div>
        </UitkSpacing>
      </div>
    );
  })
);

export default DestinationVideo;
